import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';

export default function ProfilePage() {
  const { user, token } = useAuth();
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Profile edit state
  const [isEditing, setIsEditing] = useState(false);
  const [editName, setEditName] = useState('');
  const [editEmail, setEditEmail] = useState('');
  const [saveLoading, setSaveLoading] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(null);
  const [saveError, setSaveError] = useState(null);

  // Password change state
  const [isChangingPassword, setIsChangingPassword] = useState(false);
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [passwordLoading, setPasswordLoading] = useState(false);
  const [passwordSuccess, setPasswordSuccess] = useState(null);
  const [passwordError, setPasswordError] = useState(null);

  useEffect(() => {
    async function fetchProfile() {
      try {
        const res = await fetch('/api/profile', {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!res.ok) throw new Error('Failed to fetch profile');
        const data = await res.json();
        setProfile(data);
        // Initialize edit fields with current values
        setEditName(data.profile.name);
        setEditEmail(data.profile.email);
      } catch (err) {
        console.error('Error fetching profile:', err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    }
    if (token) fetchProfile();
  }, [token]);

  const handleSaveProfile = async () => {
    setSaveLoading(true);
    setSaveError(null);
    setSaveSuccess(null);

    try {
      const res = await fetch('/api/profile', {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ name: editName, email: editEmail }),
      });

      const data = await res.json();

      if (!res.ok) {
        throw new Error(data.error || 'Failed to update profile');
      }

      // Update local state with new values
      setProfile(prev => ({
        ...prev,
        profile: { ...prev.profile, name: data.user.name, email: data.user.email },
      }));

      setSaveSuccess('Profile updated successfully!');
      setIsEditing(false);

      // Clear success message after 3 seconds
      setTimeout(() => setSaveSuccess(null), 3000);
    } catch (err) {
      setSaveError(err.message);
    } finally {
      setSaveLoading(false);
    }
  };

  const handleCancelEdit = () => {
    setEditName(profile.profile.name);
    setEditEmail(profile.profile.email);
    setIsEditing(false);
    setSaveError(null);
  };

  const handleChangePassword = async () => {
    setPasswordLoading(true);
    setPasswordError(null);
    setPasswordSuccess(null);

    // Client-side validation
    if (!currentPassword || !newPassword || !confirmPassword) {
      setPasswordError('All password fields are required');
      setPasswordLoading(false);
      return;
    }

    if (newPassword.length < 8) {
      setPasswordError('New password must be at least 8 characters');
      setPasswordLoading(false);
      return;
    }

    if (newPassword !== confirmPassword) {
      setPasswordError('New passwords do not match');
      setPasswordLoading(false);
      return;
    }

    try {
      const res = await fetch('/api/profile/password', {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ currentPassword, newPassword }),
      });

      const data = await res.json();

      if (!res.ok) {
        throw new Error(data.error || 'Failed to change password');
      }

      setPasswordSuccess('Password changed successfully!');
      setIsChangingPassword(false);

      // Clear form
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');

      // Clear success message after 3 seconds
      setTimeout(() => setPasswordSuccess(null), 3000);
    } catch (err) {
      setPasswordError(err.message);
    } finally {
      setPasswordLoading(false);
    }
  };

  const handleCancelPasswordChange = () => {
    setCurrentPassword('');
    setNewPassword('');
    setConfirmPassword('');
    setIsChangingPassword(false);
    setPasswordError(null);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-gray-500">Loading profile...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-red-700">
        Error loading profile: {error}
      </div>
    );
  }

  const { profile: info, stats, rocks_summary } = profile;

  // Format join date
  const joinDate = info.created_at
    ? new Date(info.created_at).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
      })
    : 'Unknown';

  // Calculate rank/level based on total XP
  const calculateRank = (totalXp) => {
    if (totalXp >= 50000) return { rank: 'Platinum Elite', level: 10, color: 'text-purple-700', bg: 'bg-purple-100', border: 'border-purple-300' };
    if (totalXp >= 40000) return { rank: 'Diamond Master', level: 9, color: 'text-cyan-700', bg: 'bg-cyan-100', border: 'border-cyan-300' };
    if (totalXp >= 30000) return { rank: 'Gold Champion', level: 8, color: 'text-yellow-600', bg: 'bg-yellow-100', border: 'border-yellow-300' };
    if (totalXp >= 25000) return { rank: 'Gold Expert', level: 7, color: 'text-yellow-600', bg: 'bg-yellow-100', border: 'border-yellow-300' };
    if (totalXp >= 20000) return { rank: 'Silver Elite', level: 6, color: 'text-gray-600', bg: 'bg-gray-100', border: 'border-gray-300' };
    if (totalXp >= 15000) return { rank: 'Silver Pro', level: 5, color: 'text-gray-600', bg: 'bg-gray-100', border: 'border-gray-300' };
    if (totalXp >= 10000) return { rank: 'Bronze Elite', level: 4, color: 'text-orange-700', bg: 'bg-orange-100', border: 'border-orange-300' };
    if (totalXp >= 5000) return { rank: 'Bronze Advanced', level: 3, color: 'text-orange-700', bg: 'bg-orange-100', border: 'border-orange-300' };
    if (totalXp >= 2000) return { rank: 'Rising Star', level: 2, color: 'text-blue-700', bg: 'bg-blue-100', border: 'border-blue-300' };
    return { rank: 'Apprentice', level: 1, color: 'text-green-700', bg: 'bg-green-100', border: 'border-green-300' };
  };

  const currentRank = calculateRank(stats.total_xp);
  const nextRankXp = {
    1: 2000, 2: 5000, 3: 10000, 4: 15000, 5: 20000, 6: 25000, 7: 30000, 8: 40000, 9: 50000, 10: 50000
  }[currentRank.level];
  const progressToNext = currentRank.level < 10
    ? Math.round(((stats.total_xp - (currentRank.level === 1 ? 0 : nextRankXp - 5000)) / (nextRankXp - (currentRank.level === 1 ? 0 : nextRankXp - 5000))) * 100)
    : 100;

  // Category display names and colors
  const categoryStyles = {
    'before the sale': { bg: 'bg-green-100', text: 'text-green-800', border: 'border-green-300' },
    'during the sale': { bg: 'bg-yellow-100', text: 'text-yellow-800', border: 'border-yellow-300' },
    'after the sale': { bg: 'bg-blue-100', text: 'text-blue-800', border: 'border-blue-300' },
  };

  return (
    <div className="max-w-2xl mx-auto">
      <h1 className="text-2xl font-bold text-gray-800 dark:text-white mb-6">Profile</h1>

      {/* Member Info Card */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6 mb-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold text-gray-800 dark:text-white flex items-center gap-2">
            <span className="text-xl">👤</span> Member Information
          </h2>
          {!isEditing && (
            <button
              onClick={() => setIsEditing(true)}
              className="text-sm px-3 py-1.5 bg-primary text-white rounded-lg hover:bg-primary-dark font-medium"
              data-testid="edit-profile-btn"
            >
              Edit Profile
            </button>
          )}
        </div>

        {/* Success/Error messages */}
        {saveSuccess && (
          <div className="mb-4 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg p-3 text-green-700 dark:text-green-300 text-sm">
            {saveSuccess}
          </div>
        )}
        {saveError && (
          <div className="mb-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-3 text-red-700 dark:text-red-300 text-sm">
            {saveError}
          </div>
        )}

        {/* View mode */}
        {!isEditing && (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <span className="text-sm text-gray-500 dark:text-gray-400">Name</span>
              <p className="font-medium text-gray-800 dark:text-white">{info.name}</p>
            </div>
            <div>
              <span className="text-sm text-gray-500 dark:text-gray-400">Email</span>
              <p className="font-medium text-gray-800 dark:text-white">{info.email}</p>
            </div>
            <div>
              <span className="text-sm text-gray-500 dark:text-gray-400">Role</span>
              <p className="font-medium text-gray-800 dark:text-white capitalize">{info.role}</p>
            </div>
            <div>
              <span className="text-sm text-gray-500 dark:text-gray-400">Member Since</span>
              <p className="font-medium text-gray-800 dark:text-white">{joinDate}</p>
            </div>
          </div>
        )}

        {/* Edit mode */}
        {isEditing && (
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Name
              </label>
              <input
                type="text"
                value={editName}
                onChange={(e) => setEditName(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent dark:bg-gray-700 dark:text-white"
                data-testid="edit-name-input"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Email
              </label>
              <input
                type="email"
                value={editEmail}
                onChange={(e) => setEditEmail(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent dark:bg-gray-700 dark:text-white"
                data-testid="edit-email-input"
              />
            </div>
            <div className="flex items-center justify-between pt-2">
              <div className="text-sm text-gray-500 dark:text-gray-400">
                Role: <span className="font-medium text-gray-700 dark:text-gray-300 capitalize">{info.role}</span>
              </div>
              <div className="flex gap-2">
                <button
                  onClick={handleCancelEdit}
                  disabled={saveLoading}
                  className="px-4 py-2 text-gray-700 dark:text-gray-300 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50"
                  data-testid="cancel-edit-btn"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSaveProfile}
                  disabled={saveLoading || !editName.trim() || !editEmail.trim()}
                  className="px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary-dark disabled:opacity-50 flex items-center gap-2"
                  data-testid="save-profile-btn"
                >
                  {saveLoading ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                      Saving...
                    </>
                  ) : (
                    'Save Changes'
                  )}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Password Change Card */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6 mb-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold text-gray-800 dark:text-white flex items-center gap-2">
            <span className="text-xl">🔒</span> Change Password
          </h2>
          {!isChangingPassword && (
            <button
              onClick={() => setIsChangingPassword(true)}
              className="text-sm px-3 py-1.5 bg-primary text-white rounded-lg hover:bg-primary-dark font-medium"
              data-testid="change-password-btn"
            >
              Change Password
            </button>
          )}
        </div>

        {/* Success/Error messages */}
        {passwordSuccess && (
          <div className="mb-4 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg p-3 text-green-700 dark:text-green-300 text-sm">
            {passwordSuccess}
          </div>
        )}
        {passwordError && (
          <div className="mb-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-3 text-red-700 dark:text-red-300 text-sm">
            {passwordError}
          </div>
        )}

        {/* View mode */}
        {!isChangingPassword && (
          <div className="text-sm text-gray-500 dark:text-gray-400">
            Click "Change Password" to update your password. You will need to enter your current password for security.
          </div>
        )}

        {/* Change mode */}
        {isChangingPassword && (
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Current Password
              </label>
              <input
                type="password"
                value={currentPassword}
                onChange={(e) => setCurrentPassword(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent dark:bg-gray-700 dark:text-white"
                data-testid="current-password-input"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                New Password (min. 8 characters)
              </label>
              <input
                type="password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent dark:bg-gray-700 dark:text-white"
                data-testid="new-password-input"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Confirm New Password
              </label>
              <input
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent dark:bg-gray-700 dark:text-white"
                data-testid="confirm-password-input"
              />
            </div>
            <div className="flex items-center justify-end pt-2">
              <div className="flex gap-2">
                <button
                  onClick={handleCancelPasswordChange}
                  disabled={passwordLoading}
                  className="px-4 py-2 text-gray-700 dark:text-gray-300 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50"
                  data-testid="cancel-password-btn"
                >
                  Cancel
                </button>
                <button
                  onClick={handleChangePassword}
                  disabled={passwordLoading || !currentPassword || !newPassword || !confirmPassword}
                  className="px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary-dark disabled:opacity-50 flex items-center gap-2"
                  data-testid="save-password-btn"
                >
                  {passwordLoading ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                      Changing...
                    </>
                  ) : (
                    'Change Password'
                  )}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Rank/Level Card */}
      <div className={`bg-white dark:bg-gray-800 rounded-lg shadow-sm border ${currentRank.border} dark:border-gray-700 p-6 mb-6`}>
        <h2 className="text-lg font-semibold text-gray-800 dark:text-white mb-4 flex items-center gap-2">
          <span className="text-xl">🏅</span> Rank & Level
        </h2>
        <div className={`${currentRank.bg} dark:bg-opacity-20 rounded-lg p-4 text-center border ${currentRank.border}`}>
          <p className="text-xs uppercase tracking-wider opacity-70 mb-1">Level {currentRank.level}</p>
          <p className={`text-2xl font-bold ${currentRank.color} dark:text-opacity-90 mb-2`}>{currentRank.rank}</p>
          <div className="flex items-center justify-center gap-2 text-sm text-gray-600 dark:text-gray-400">
            <span>{stats.total_xp.toLocaleString()} XP</span>
            {currentRank.level < 10 && (
              <>
                <span>•</span>
                <span>{nextRankXp.toLocaleString()} XP to next level</span>
              </>
            )}
          </div>
          {currentRank.level < 10 && (
            <div className="mt-3 w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2">
              <div
                className={`h-2 rounded-full transition-all duration-500 ${currentRank.color.replace('text-', 'bg-')}`}
                style={{ width: `${Math.min(progressToNext, 100)}%` }}
              ></div>
            </div>
          )}
        </div>
      </div>

      {/* Stats Card */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6 mb-6">
        <h2 className="text-lg font-semibold text-gray-800 dark:text-white mb-4 flex items-center gap-2">
          <span className="text-xl">⚡</span> Stats
        </h2>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <div className="bg-gradient-to-br from-indigo-50 to-purple-50 dark:from-indigo-900/30 dark:to-purple-900/30 rounded-lg p-4 text-center border border-indigo-100 dark:border-indigo-800">
            <p className="text-sm text-indigo-600 dark:text-indigo-400 font-medium">Total XP</p>
            <p className="text-3xl font-bold text-indigo-700 dark:text-indigo-300">{stats.total_xp.toLocaleString()}</p>
          </div>
          <div className="bg-gradient-to-br from-orange-50 to-red-50 dark:from-orange-900/30 dark:to-red-900/30 rounded-lg p-4 text-center border border-orange-100 dark:border-orange-800">
            <p className="text-sm text-orange-600 dark:text-orange-400 font-medium">Current Streak</p>
            <p className="text-3xl font-bold text-orange-700 dark:text-orange-300 flex items-center justify-center gap-1">
              {stats.current_streak > 0 && <span>🔥</span>}
              {stats.current_streak} <span className="text-base font-normal">days</span>
            </p>
          </div>
          <div className="bg-gradient-to-br from-yellow-50 to-amber-50 dark:from-yellow-900/30 dark:to-amber-900/30 rounded-lg p-4 text-center border border-yellow-100 dark:border-yellow-800">
            <p className="text-sm text-yellow-600 dark:text-yellow-400 font-medium">Best Streak</p>
            <p className="text-3xl font-bold text-yellow-700 dark:text-yellow-300 flex items-center justify-center gap-1">
              {stats.best_streak > 0 && <span>🏆</span>}
              {stats.best_streak} <span className="text-base font-normal">days</span>
            </p>
          </div>
        </div>
      </div>

      {/* Quarterly Rocks Summary */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6">
        <h2 className="text-lg font-semibold text-gray-800 dark:text-white mb-4 flex items-center gap-2">
          <span className="text-xl">🪨</span> Quarterly Rocks ({rocks_summary.quarter})
        </h2>
        {rocks_summary.rocks.length === 0 ? (
          <p className="text-gray-500 dark:text-gray-400 italic">
            No rocks set for this quarter yet. Visit the Quarterly Rocks page to create them.
          </p>
        ) : (
          <div className="space-y-3">
            <p className="text-sm text-gray-600 dark:text-gray-400">
              {rocks_summary.completed} of {rocks_summary.total} rocks completed
            </p>
            {rocks_summary.rocks.map((rock) => {
              const style = categoryStyles[rock.category] || {
                bg: 'bg-gray-100',
                text: 'text-gray-800',
                border: 'border-gray-300',
              };
              return (
                <div
                  key={rock.id}
                  className={`flex items-center justify-between p-3 rounded-lg border ${style.border} ${style.bg}`}
                >
                  <div>
                    <p className={`font-medium ${style.text}`}>{rock.name}</p>
                    <p className={`text-xs ${style.text} opacity-70 capitalize`}>{rock.category}</p>
                  </div>
                  <div className="text-right">
                    {rock.is_completed ? (
                      <span className="inline-flex items-center gap-1 text-green-600 font-medium text-sm">
                        ✅ Completed
                      </span>
                    ) : (
                      <span className="text-sm text-gray-500">
                        Due: {new Date(rock.due_date).toLocaleDateString()}
                      </span>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
